// src/api.ts
// Complete API client for DevGPT frontend
// v3.4.0: Added feedback_id support for enhanced analytics

import { fetchWithAuth } from './api/fetchWithAuth';

// Import types from the shared types file
import type {
  Message,
  Chat,
  HealthStatus,
  SyncStatus
} from './types';

// Re-export for convenience
export type { Message, Chat, HealthStatus, SyncStatus };

// API-specific types
export interface ChatRequest {
  chat_id?: string;
  message: string;
  user_email?: string;
  image?: string;
  image_type?: string;
}

export interface ChatResponse {
  response: string;
  model_used: string;
  used_search: boolean;
  used_vision?: boolean;
  sources: string[];
  cached: boolean;
  tokens_estimated: number;
  chat_id: string;
  timestamp: string;
  feedback_id?: string;        // NEW v3.4.0: Feedback tracking ID
  queries_generated?: number;  // NEW v3.4.0: Number of query variations
}

// v3.4.0: Updated to support both new feedback_id and legacy formats
export interface FeedbackRequest {
  // New v3.4.0 format (preferred)
  feedback_id?: string;
  // Legacy format (fallback)
  chat_id?: string;
  message_index?: number;
  // Common fields
  rating: 'positive' | 'negative';
  user_email?: string;
  comment?: string;
}

export interface UserInfo {
  email: string;
  id: string;
  authenticated: boolean;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : "/api";

export const api = {
  // ============================================
  // CHAT METHODS
  // ============================================

  /**
   * Send a message and get a response (non-streaming)
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const body: Record<string, unknown> = {
      message: request.message,
      chat_id: request.chat_id,
      user_email: request.user_email,
    };

    // Add image if provided
    if (request.image) {
      // Remove the data:image/xxx;base64, prefix if present
      let base64Data = request.image;
      if (base64Data.includes(",")) {
        base64Data = base64Data.split(",")[1];
      }
      body.image = base64Data;
      body.image_type = request.image_type || "image/png";
    }

    const response = await fetchWithAuth(`${API_BASE}/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Stream a message response (async generator)
   * Note: Current backend returns JSON, not SSE stream
   * This simulates streaming by returning the full response
   */
  async *streamMessage(request: ChatRequest): AsyncGenerator<string, void, unknown> {
    try {
      const response = await this.sendMessage(request);
      
      // Simulate streaming by yielding the response
      // In the future, this could be replaced with actual SSE streaming
      yield response.response;
      
    } catch (error) {
      throw error;
    }
  },

  /**
   * Legacy streaming method for backward compatibility
   */
  async streamChatCompletion(
    messages: Message[],
    onChunk: (delta: string) => void
  ): Promise<void> {
    const lastMessage = messages.filter((m) => m.role === "user").pop();
    if (!lastMessage) {
      throw new Error("No user message found");
    }

    const response = await this.sendMessage({
      message: lastMessage.content,
      image: lastMessage.image,
    });

    onChunk(response.response);
  },

  // ============================================
  // CHAT HISTORY METHODS
  // ============================================

  /**
   * Get all chats for a user
   */
  async getChats(userEmail: string): Promise<Chat[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/chats?user_email=${encodeURIComponent(userEmail)}`, {
        credentials: "include",
      });

      if (!response.ok) {
        // Return empty array if endpoint doesn't exist yet
        if (response.status === 404) {
          return [];
        }
        throw new Error(`Failed to get chats: ${response.status}`);
      }

      const data = await response.json();
      return data.chats || [];
    } catch (error) {
      console.error("Failed to load chats:", error);
      return [];
    }
  },

  /**
   * Get a specific chat by ID
   */
  async getChat(chatId: string): Promise<Chat> {
    const response = await fetchWithAuth(`${API_BASE}/chats/${chatId}`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to get chat: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Create a new chat
   */
  async createChat(userEmail: string): Promise<{ chat_id: string }> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/chats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ user_email: userEmail }),
      });

      if (!response.ok) {
        // Generate a local chat ID if endpoint doesn't exist
        if (response.status === 404) {
          return { chat_id: `chat-${Date.now()}` };
        }
        throw new Error(`Failed to create chat: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      // Fallback to local chat ID
      return { chat_id: `chat-${Date.now()}` };
    }
  },

  /**
   * Delete a chat
   */
  async deleteChat(chatId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE}/chats/${chatId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete chat: ${response.status}`);
    }
  },

  // ============================================
  // FEEDBACK METHODS
  // ============================================

  /**
   * Submit feedback for a message
   * v3.4.0: Now supports both feedback_id (preferred) and legacy chat_id+message_index
   */
  async submitFeedback(feedback: FeedbackRequest): Promise<void> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(feedback),
      });

      if (!response.ok) {
        console.error(`Failed to submit feedback: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  },

  // ============================================
  // IMAGE METHODS
  // ============================================

  /**
   * Convert a file to base64
   */
  async uploadImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Return just the base64 data without the prefix
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  },

  /**
   * Helper to convert File to base64 with type info
   */
  async fileToBase64(file: File): Promise<{ data: string; type: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve({
          data: result,
          type: file.type || "image/png",
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  // ============================================
  // HEALTH & STATUS METHODS
  // ============================================

  /**
   * Get basic health status
   */
  async getHealth(): Promise<HealthStatus> {
    const response = await fetchWithAuth(`${API_BASE}/health`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Get detailed health status
   */
  async getDetailedHealth(): Promise<HealthStatus> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/health/detailed`, {
        credentials: "include",
      });

      if (!response.ok) {
        // Fall back to basic health if detailed endpoint doesn't exist
        if (response.status === 404) {
          return this.getHealth();
        }
        throw new Error(`Health check failed: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      // Fall back to basic health
      return this.getHealth();
    }
  },

  /**
   * Get sync status
   */
  async getSyncStatus(): Promise<SyncStatus> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/sync-status`, {
        credentials: "include",
      });

      if (!response.ok) {
        // Return default data if endpoint doesn't exist
        if (response.status === 404) {
          return {
            documents_indexed: 0,
            last_sync: new Date().toISOString(),
            file_share_status: "unknown",
            blob_storage_status: "unknown",
            search_index_status: "unknown",
          };
        }
        throw new Error(`Failed to get sync status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      // Return fallback data
      return {
        documents_indexed: 0,
        last_sync: new Date().toISOString(),
        file_share_status: "unknown",
        blob_storage_status: "unknown",
        search_index_status: "unknown",
      };
    }
  },

  // ============================================
  // USER METHODS
  // ============================================

  /**
   * Get current user info from Azure Easy Auth
   */
  async getCurrentUser(): Promise<UserInfo> {
    try {
      const response = await fetchWithAuth(`${API_BASE}/user`, {
        credentials: "include",
      });

      if (!response.ok) {
        return { email: "unknown", id: "unknown", authenticated: false };
      }

      return response.json();
    } catch (error) {
      return { email: "unknown", id: "unknown", authenticated: false };
    }
  },
};